import { TooltipWrapper } from "@/components/common/tooltip";
import {
  useConvertDart,
  useConvertDsdOrExcel,
} from "@/hooks/mutations/use-dsd-convert";
import { useAcceptedFileSelect } from "@/hooks/use-accepted-file-select";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/styles/ui/dropdown-menu";
import Button from "@/toggle-ui-kit/button";
import { getFileExtension } from "@/utils/file-accept";
import {
  ChevronDown,
  Download,
  Files,
  FileSpreadsheet,
  FileText,
  Link,
  Loader2,
  X,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";

interface DsdXlsxConvertPageProps {
  className?: string;
}

const ACCEPTED_EXTENSIONS = [".dsd", ".xlsx"];
const ACCEPT = ACCEPTED_EXTENSIONS.join(",");

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** 확장자에 따라 아이콘과 색상을 반환 */
function getFileTypeInfo(filename: string) {
  const ext = getFileExtension(filename);
  if (ext === ".xlsx") {
    return {
      label: "XLSX",
      icon: FileSpreadsheet,
      bgColor: "bg-green-50",
      textColor: "text-green-700",
      iconColor: "text-green-500",
      borderColor: "border-green-200",
    };
  }
  return {
    label: "DSD",
    icon: FileText,
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    iconColor: "text-blue-500",
    borderColor: "border-blue-200",
  };
}

export const DsdXlsxConvertPage = ({ className }: DsdXlsxConvertPageProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dartUrl, setDartUrl] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  const { mutate, isPending } = useConvertDsdOrExcel();
  const { mutate: mutateUrl, isPending: isUrlPending } = useConvertDart();

  const clearFile = useCallback(() => {
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const handleFileSelect = useAcceptedFileSelect({
    accept: ACCEPT,
    inputRef,
    onFileSelect: setFile,
  });

  // 드래그 앤 드롭 핸들러
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);

      const droppedFile = e.dataTransfer.files?.[0] ?? null;
      handleFileSelect(droppedFile);
    },
    [handleFileSelect],
  );

  const handleClickUploadZone = () => {
    inputRef.current?.click();
  };

  const fileTypeInfo = file ? getFileTypeInfo(file.name) : null;

  return (
    <div className={cn("flex h-full w-full", className)}>
      <div className="flex-1 flex items-center justify-center bg-white transition-all duration-300">
        <div className="flex flex-col max-w-[840px] w-full gap-8 justify-center items-start px-6">
          {/* 헤더 섹션 */}
          <div className="flex flex-col gap-2 w-full">
            <div className="flex items-center gap-2">
              <h1 className="title-2-bold text-primary">DSD 엑셀 변환</h1>
            </div>
            <p className="body-1-medium text-neutral-500">
              DSD 파일을 엑셀 파일로, 엑셀 파일을 DSD 파일로 변환할 수 있습니다.
            </p>
          </div>{" "}
          {/* URL 입력 섹션 */}
          <div className="flex flex-col gap-3 w-full">
            <div className="flex items-center gap-2">
              <Link className="w-4 h-4 text-neutral-500" />
              <p className="body-2-medium text-neutral-700">
                DART 공시 URL로 다운로드
              </p>
            </div>
            <div className="flex items-center gap-2 w-full">
              <input
                type="url"
                value={dartUrl}
                onChange={(e) => setDartUrl(e.target.value)}
                placeholder="DART 공시 URL을 입력해주세요"
                className={cn(
                  "flex-1 h-9 px-3 rounded-lg border border-neutral-200 bg-white",
                  "body-2-medium text-neutral-800 placeholder:text-neutral-400",
                  "outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400",
                  "transition-all duration-150",
                )}
              />
              <DropdownMenu>
                <DropdownMenuTrigger
                  asChild
                  disabled={!dartUrl.trim() || isUrlPending}
                >
                  <Button
                    variant="solid"
                    size={36}
                    className="cursor-pointer disabled:cursor-not-allowed"
                    leftIcon={
                      !isUrlPending ? (
                        <Download className="w-4 h-4" />
                      ) : (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      )
                    }
                    rightIcon={<ChevronDown className="w-4 h-4" />}
                    disabled={!dartUrl.trim() || isUrlPending}
                  >
                    {isUrlPending ? "변환 중..." : "다운로드"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="min-w-[--radix-dropdown-menu-trigger-width] w-[--radix-dropdown-menu-trigger-width]"
                >
                  <DropdownMenuItem
                    className="gap-2 cursor-pointer"
                    onSelect={() =>
                      mutateUrl(
                        { url: dartUrl.trim(), toFormat: "xlsx" },
                        { onSuccess: () => setDartUrl("") },
                      )
                    }
                  >
                    <FileSpreadsheet className="w-4 h-4 text-green-500" />
                    엑셀 파일
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="gap-2 cursor-pointer"
                    onSelect={() =>
                      mutateUrl(
                        { url: dartUrl.trim(), toFormat: "dsd" },
                        { onSuccess: () => setDartUrl("") },
                      )
                    }
                  >
                    <FileText className="w-4 h-4 text-blue-500" />
                    DSD 파일
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {/* 구분선 */}
          <div className="flex items-center gap-3 w-full">
            <div className="flex-1 h-px bg-neutral-200" />
            <span className="caption-1-regular text-neutral-400">또는</span>
            <div className="flex-1 h-px bg-neutral-200" />
          </div>
          {/* 파일 업로드 영역 */}
          <div className="flex flex-col gap-3 w-full">
            {/* 숨겨진 파일 인풋 */}
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files?.[0] ?? null)}
            />

            {!file ? (
              /* 드래그 앤 드롭 업로드 존 */
              <button
                type="button"
                onClick={handleClickUploadZone}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={cn(
                  "group relative flex flex-col items-center justify-center gap-3 w-full py-12 px-6",
                  "rounded-xl border-2 border-dashed cursor-pointer",
                  "transition-all duration-200 ease-in-out",
                  isDragging
                    ? "border-blue-400 bg-blue-50/60 scale-[1.01]"
                    : "border-neutral-200 bg-neutral-50/50 hover:border-blue-300 hover:bg-blue-50/30",
                )}
              >
                <div className="flex flex-col items-center gap-1">
                  <p
                    className={cn(
                      "body-2-medium transition-colors duration-200",
                      isDragging ? "text-blue-600" : "text-neutral-700",
                    )}
                  >
                    {isDragging
                      ? "여기에 파일을 놓아주세요"
                      : "클릭하거나 파일을 드래그하여 업로드"}
                  </p>
                  <p className="caption-1-regular text-neutral-400">
                    DSD, XLSX 파일만 지원됩니다
                  </p>
                </div>
              </button>
            ) : (
              /* 파일 선택 완료 상태 */
              <div
                className={cn(
                  "flex items-center gap-4 w-full p-4 rounded-xl border transition-all duration-200",
                  fileTypeInfo?.borderColor,
                  fileTypeInfo?.bgColor,
                  "bg-opacity-40",
                )}
              >
                {/* 파일 아이콘 */}
                <div
                  className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-lg shrink-0",
                    fileTypeInfo?.bgColor,
                  )}
                >
                  {fileTypeInfo?.icon && (
                    <fileTypeInfo.icon
                      className={cn("w-5 h-5", fileTypeInfo.iconColor)}
                    />
                  )}
                </div>

                {/* 파일 정보 */}
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                  <p className="body-2-medium text-neutral-800 truncate">
                    {file.name}
                  </p>
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "caption-2-semibold px-1.5 py-0.5 rounded",
                        fileTypeInfo?.bgColor,
                        fileTypeInfo?.textColor,
                      )}
                    >
                      {fileTypeInfo?.label}
                    </span>
                    <span className="caption-2-regular text-neutral-400">
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                </div>

                {/* 파일 제거 버튼 */}
                <button
                  type="button"
                  onClick={clearFile}
                  className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors duration-150"
                  aria-label="파일 제거"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          {/* 파일 변환 다운로드 버튼 */}
          <div className="flex items-end justify-end gap-2 w-full">
            <TooltipWrapper
              label="클릭하여 다운로드 받을 수 있습니다."
              side="left"
              arrowed
              styles={{
                contentStyle: "border-none p-0",
                bgStyle: "bg-neutral-900 px-1.5 py-1",
                textStyle: "label-2-medium text-white",
                arrowStyle: "fill-neutral-900",
              }}
            >
              <Button
                variant="solid"
                size={36}
                leftIcon={
                  !isPending ? (
                    <Files className="w-4 h-4" />
                  ) : (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )
                }
                disabled={!file || isPending}
                onClick={() => file && mutate(file, { onSuccess: clearFile })}
              >
                {isPending ? "변환 중..." : "다운로드"}
              </Button>
            </TooltipWrapper>
          </div>
        </div>
      </div>
    </div>
  );
};
